import { useState, useCallback, useMemo } from 'react'
import type { MergeResult } from '@/services/merge'
import type { ResolutionSet, Choice } from '@/services/merge/resolve'

function buildDefault(mergeResult: MergeResult): ResolutionSet['resolutions'] {
  const resolutions: ResolutionSet['resolutions'] = {}
  for (const c of mergeResult.conflicts) {
    resolutions[c.id] = { conflictId: c.id, choice: c.suggested }
  }
  return resolutions
}

export function useConflictResolution(mergeResult: MergeResult) {
  const [resolutions, setResolutions] = useState<ResolutionSet['resolutions']>(
    () => buildDefault(mergeResult),
  )

  const setChoice = useCallback((conflictId: string, choice: Choice) => {
    setResolutions((prev) => ({
      ...prev,
      [conflictId]: { ...prev[conflictId], conflictId, choice },
    }))
  }, [])

  const setFieldChoice = useCallback((conflictId: string, field: string, choice: Choice) => {
    setResolutions((prev) => {
      const existing = prev[conflictId] ?? { conflictId, choice: 'theirs' }
      return {
        ...prev,
        [conflictId]: {
          ...existing,
          fieldChoices: { ...existing.fieldChoices, [field]: choice },
        },
      }
    })
  }, [])

  const acceptAllMine = useCallback(() => {
    const next: ResolutionSet['resolutions'] = {}
    for (const c of mergeResult.conflicts) {
      next[c.id] = { conflictId: c.id, choice: 'mine' }
    }
    setResolutions(next)
  }, [mergeResult.conflicts])

  const acceptAllTheirs = useCallback(() => {
    const next: ResolutionSet['resolutions'] = {}
    for (const c of mergeResult.conflicts) {
      next[c.id] = { conflictId: c.id, choice: 'theirs' }
    }
    setResolutions(next)
  }, [mergeResult.conflicts])

  const resolvedCount = useMemo(
    () => mergeResult.conflicts.filter((c) => resolutions[c.id] !== undefined).length,
    [mergeResult.conflicts, resolutions],
  )

  const allResolved = resolvedCount === mergeResult.conflicts.length

  const resolutionSet: ResolutionSet = useMemo(() => ({ resolutions }), [resolutions])

  return {
    resolutions,
    resolutionSet,
    allResolved,
    resolvedCount,
    totalConflicts: mergeResult.conflicts.length,
    setChoice,
    setFieldChoice,
    acceptAllMine,
    acceptAllTheirs,
  }
}
